import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';

const PaymentsList = () => {
  const [payments, setPayments] = useState([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shipmentModal, setShipmentModal] = useState({ open: false, loading: false, shipment: null, error: null });
  const [exportModal, setExportModal] = useState({
    open: false,
    startDate: '',
    endDate: '',
    selectedService: '',
    selectedCategory: '',
    selectedAct: '',
    exportFormat: 'csv'
  });
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [acts, setActs] = useState([]);
  // État utilisé pour stocker temporairement les données à exporter
  // eslint-disable-next-line no-unused-vars
  const [exportData, setExportData] = useState([]);
  const exportDataRef = useRef([]);
  // Recherche et pagination
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  // Variable pour savoir si on utilise le filtrage côté serveur
  const [isServerFiltered, setIsServerFiltered] = useState(false);
  
  // Filtrage côté client (pour la pagination uniquement)
  // Tous les filtres sont déjà appliqués dans la fonction applyFilters
  const filteredPayments = payments;

  const totalPages = Math.ceil(filteredPayments.length / perPage);
  const paginatedPayments = filteredPayments.slice((page - 1) * perPage, page * perPage);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    setPage(1);
    
    // Annuler le timeout précédent s'il existe
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Définir un nouveau timeout pour retarder la recherche
    searchTimeout.current = setTimeout(() => {
      console.log('Recherche déclenchée avec la valeur:', searchValue);
      // Appliquer les filtres avec la nouvelle valeur de recherche
      applyFilters(statusFilter, methodFilter);
    }, 500);
  };
  
  // Référence pour le timeout de recherche
  const searchTimeout = useRef(null);

  const handleStatusFilter = (e) => {
    const newStatus = e.target.value;
    setStatusFilter(newStatus);
    setPage(1);
    // Appliquer immédiatement le filtre
    applyFilters(newStatus, methodFilter);
  };

  const handleMethodFilter = (e) => {
    const newMethod = e.target.value;
    setMethodFilter(newMethod);
    setPage(1);
    // Appliquer immédiatement le filtre
    applyFilters(statusFilter, newMethod);
  };
  
  // Fonction pour appliquer les filtres
  const applyFilters = (status, method) => {
  console.log('Appliquer les filtres:', { status, method, search });
  // Activer le filtrage côté serveur
  setIsServerFiltered(true); // On active le filtrage côté serveur pour tous les filtres
  setLoading(true);
  
  // Mettre à jour les états de filtres avant d'appeler fetchPayments
  // pour s'assurer que les valeurs les plus récentes sont utilisées
  setStatusFilter(status);
  setMethodFilter(method);
  
  // Utiliser setTimeout pour s'assurer que les états sont mis à jour avant d'appeler fetchPayments
  setTimeout(() => {
    // Toujours utiliser le filtrage côté serveur pour les recherches optimisées
    // Notre nouvelle implémentation gère correctement les recherches textuelles
    console.log('Utilisation du filtrage côté serveur: true');
    fetchPayments(true);
  }, 0);
};

  const handlePerPage = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  // Handler pour ouvrir la modale et charger les infos d'expédition
  const handleShowShipment = async (shipmentId) => {
    setShipmentModal({ open: true, loading: true, shipment: null, error: null });
    try {
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .eq('id', shipmentId)
        .single();
      if (error) {
        setShipmentModal({ open: true, loading: false, shipment: null, error: error.message });
        return;
      }
      setShipmentModal({ open: true, loading: false, shipment: data, error: null });
    } catch (err) {
      setShipmentModal({ open: true, loading: false, shipment: null, error: err.message });
    }
  };
  // Handler pour fermer la modale
  const handleCloseShipment = () => {
    setShipmentModal(prev => ({ ...prev, open: false }));
  };

  // Fonction pour déterminer la classe CSS en fonction du statut de l'expédition
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour récupérer le nombre total de paiements
  const fetchTotalPayments = async () => {
    try {
      const { count, error } = await supabase
        .from('payments')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      
      setTotalPayments(count || 0);
      
      // Si aucun filtre n'est actif, le nombre total de paiements est égal au nombre de paiements affichés
      if (!search && !statusFilter && !methodFilter) {
        setPayments(prevPayments => {
          // Si nous n'avons pas encore chargé les paiements, ne rien faire
          if (prevPayments.length === 0 && !loading) {
            fetchPayments(true);
          }
          return prevPayments;
        });
      }
    } catch (countError) {
      console.error('Erreur lors du comptage des paiements:', countError);
    }
  };

  const fetchPayments = async (useServerFilter = false) => {
  setLoading(true);
  setError(null);
  setIsServerFiltered(useServerFilter); // Mettre à jour l'état pour indiquer si on utilise le filtrage côté serveur
  
  try {
    console.log('Début de fetchPayments avec useServerFilter =', useServerFilter);
    console.log('Filtres actuels:', { statusFilter, methodFilter, search });
    
    // Si nous avons une recherche textuelle et que nous ne pouvons pas filtrer par montant
    // nous devons utiliser une approche différente pour optimiser la requête
    const searchTerm = search ? search.trim() : '';
    const isNumericSearch = !isNaN(parseFloat(searchTerm)) && isFinite(searchTerm);
    
    // Si nous avons une recherche textuelle, nous devons d'abord chercher les utilisateurs correspondants
    if (searchTerm && !isNumericSearch && useServerFilter) {
      console.log('Recherche textuelle détectée, utilisation de la recherche avancée');
      
      // 1. Rechercher les utilisateurs qui correspondent à la recherche
      const { data: matchingUsers, error: userError } = await supabase
        .from('users')
        .select('id')
        .or(`firstname.ilike.%${searchTerm}%,lastname.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`);
      
      if (userError) {
        console.error('Erreur lors de la recherche des utilisateurs:', userError);
        throw userError;
      }
      
      console.log(`Utilisateurs correspondants trouvés: ${matchingUsers ? matchingUsers.length : 0}`);
      
      // 2. Construire la requête pour les paiements
      let query = supabase
        .from('payments')
        .select(`
          *,
          users:user_id (
            id,
            firstname,
            lastname,
            email,
            phone,
            address,
            city,
            zipcode,
            country,
            host_name,
            created_at,
            updated_at
          ),
          shipments!payment_id (*)
        `);
      
      // 3. Filtrer par utilisateurs correspondants ou par transaction_id/status/method
      if (matchingUsers && matchingUsers.length > 0) {
        const userIds = matchingUsers.map(user => user.id);
        console.log('Filtrage par IDs utilisateurs:', userIds);
        
        // Combiner la recherche utilisateur avec la recherche sur les champs de paiement
        query = query.or(`user_id.in.(${userIds.join(',')}),transaction_id.ilike.%${searchTerm}%,payment_status.ilike.%${searchTerm}%,payment_method.ilike.%${searchTerm}%`);
      } else {
        // Si aucun utilisateur ne correspond, chercher uniquement dans les champs de paiement
        console.log('Aucun utilisateur correspondant, recherche dans les champs de paiement uniquement');
        query = query.or(`transaction_id.ilike.%${searchTerm}%,payment_status.ilike.%${searchTerm}%,payment_method.ilike.%${searchTerm}%`);
      }
      
      // Appliquer les filtres supplémentaires
      if (statusFilter) {
        console.log('Application du filtre de statut:', statusFilter);
        query = query.eq('payment_status', statusFilter);
      }
      
      if (methodFilter) {
        console.log('Application du filtre de méthode:', methodFilter);
        query = query.eq('payment_method', methodFilter);
      }
      
      // Filtrage par date si spécifié dans le modal d'exportation
      if (exportModal.startDate && exportModal.endDate) {
        console.log('Filtrage par date:', exportModal.startDate, 'à', exportModal.endDate);
        query = query
          .gte('created_at', format(new Date(exportModal.startDate), 'yyyy-MM-dd'))
          .lte('created_at', format(new Date(exportModal.endDate), 'yyyy-MM-dd'));
      }
      
      // Appliquer l'ordre
      query = query.order('created_at', { ascending: false });
      
      // Afficher l'URL de la requête pour débogage
      console.log('URL de la requête optimisée:', query.url);
      
      // Exécuter la requête
      const { data: paymentsData, error: queryError } = await query;
      
      if (queryError) {
        console.error('Erreur de requête Supabase:', queryError);
        throw queryError;
      }
      
      console.log(`Nombre de paiements récupérés: ${paymentsData ? paymentsData.length : 0}`);
      setPayments(paymentsData || []);
    } 
    else {
      // Approche standard pour les autres types de recherche
      console.log('Utilisation de l\'approche standard de filtrage');
      
      // Construire la requête de base
      let query = supabase
        .from('payments')
        .select(`
          *,
          users:user_id (
            id,
            firstname,
            lastname,
            email,
            phone,
            address,
            city,
            zipcode,
            country,
            host_name,
            created_at,
            updated_at
          ),
          shipments!payment_id (*)
        `);
      
      // Appliquer les filtres de statut et méthode s'ils sont définis
      if (statusFilter) {
        console.log('Application du filtre de statut:', statusFilter);
        query = query.eq('payment_status', statusFilter);
      }
      
      if (methodFilter) {
        console.log('Application du filtre de méthode:', methodFilter);
        query = query.eq('payment_method', methodFilter);
      }
      
      // Si une recherche est spécifiée et qu'on utilise le filtrage côté serveur
      if (searchTerm && useServerFilter) {
        if (isNumericSearch) {
          // Si c'est un nombre, filtrer par montant
          const amountSearch = parseFloat(searchTerm);
          console.log('Recherche par montant:', amountSearch);
          query = query.eq('amount', amountSearch);
        } else {
          // Pour les recherches textuelles simples, essayer sur transaction_id
          console.log('Recherche textuelle simple sur transaction_id');
          query = query.ilike('transaction_id', `%${searchTerm}%`);
        }
      }
      
      // Filtrage par date si spécifié dans le modal d'exportation
      if (exportModal.startDate && exportModal.endDate) {
        console.log('Filtrage par date:', exportModal.startDate, 'à', exportModal.endDate);
        query = query
          .gte('created_at', format(new Date(exportModal.startDate), 'yyyy-MM-dd'))
          .lte('created_at', format(new Date(exportModal.endDate), 'yyyy-MM-dd'));
      }
      
      // Appliquer l'ordre après les filtres
      query = query.order('created_at', { ascending: false });
      
      // Afficher l'URL de la requête pour débogage
      console.log('URL de la requête standard:', query.url);
    
      // Exécuter la requête
      const { data: paymentsData, error: queryError } = await query;
      
      if (queryError) {
        console.error('Erreur de requête Supabase:', queryError);
        throw queryError;
      }
      
      console.log(`Nombre de paiements récupérés: ${paymentsData ? paymentsData.length : 0}`);
      
      // Appliquer le filtrage côté client pour la recherche textuelle si nécessaire
      let filteredData = paymentsData || [];
      
      if (searchTerm && !useServerFilter && filteredData.length > 0) {
        console.log('Application du filtrage côté client pour la recherche textuelle');
        const searchLower = searchTerm.toLowerCase();
        
        filteredData = filteredData.filter(p => {
          // Vérifier si l'utilisateur existe
          if (!p.users) return false;
          
          const user = p.users;
          
          // Recherche par informations utilisateur
          const matchesUserInfo = 
            (user.firstname && user.firstname.toLowerCase().includes(searchLower)) ||
            (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
            (user.email && user.email.toLowerCase().includes(searchLower)) ||
            (user.phone && user.phone.toLowerCase().includes(searchLower));
          
          // Recherche par informations de paiement
          const matchesPaymentInfo = 
            (p.transaction_id && p.transaction_id.toLowerCase().includes(searchLower)) ||
            (p.amount && String(p.amount).includes(searchLower)) ||
            (p.payment_status && p.payment_status.toLowerCase().includes(searchLower)) ||
            (p.payment_method && p.payment_method.toLowerCase().includes(searchLower));
          
          return matchesUserInfo || matchesPaymentInfo;
        });
        
        console.log(`Après filtrage côté client: ${filteredData.length} paiements`);
      }
      
      // Définir les paiements récupérés et filtrés
      setPayments(filteredData);
    }
  } catch (fetchError) {
    console.error('Erreur lors de la récupération des paiements:', fetchError);
    setError(fetchError.message);
  } finally {
    setLoading(false);
  }
};

  // Appel de fetchPayments au montage et récupération du nombre total de paiements
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPayments(true); // Utiliser le filtrage côté serveur par défaut
    fetchTotalPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Effet pour mettre à jour le nombre total de paiements périodiquement
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTotalPayments();
    }, 60000); // Mise à jour toutes les minutes
    
    return () => clearInterval(interval);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Chargement des services, catégories et actes consulaires
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: servicesData, error: servicesError } = await supabase
          .from('services')
          .select('*');
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*, services:service_id (name)');
        const { data: actsData, error: actsError } = await supabase
          .from('consular_acts')
          .select('*, categories:category_id (name, service_id)');
        if (servicesError) throw servicesError;
        if (categoriesError) throw categoriesError;
        if (actsError) throw actsError;
        setServices(servicesData || []);
        setCategories(categoriesData || []);
        setActs(actsData || []);
      } catch (err) {
        console.error('Erreur lors du chargement des données :', err);
      }
    };
    fetchData();
  }, []);
  const handleExport = async () => {
    try {
      let data;
      let error;
      
      // Si des filtres spécifiques aux documents sont sélectionnés
      if (exportModal.selectedService || exportModal.selectedCategory || exportModal.selectedAct) {
        // Utiliser la fonction RPC filter_payments_with_documents
        const params = {
          start_date: exportModal.startDate ? format(new Date(exportModal.startDate), 'yyyy-MM-dd') : null,
          end_date: exportModal.endDate ? format(new Date(exportModal.endDate), 'yyyy-MM-dd') : null,
          service_id: exportModal.selectedService || null,
          category_id: exportModal.selectedCategory || null,
          act_id: exportModal.selectedAct || null,
          status: statusFilter || null,
          method: methodFilter || null
        };
        
        console.log('Paramètres d\'exportation:', params);
        
        // Appeler la fonction RPC
        const rpcResult = await supabase.rpc('filter_payments_with_documents', params);
        
        if (rpcResult.error) {
          console.error('Erreur RPC:', rpcResult.error);
          throw rpcResult.error;
        }
        
        // Récupérer les informations utilisateur pour les paiements filtrés
        const paymentIds = rpcResult.data.map(p => p.id);
        const enrichResult = await supabase
          .from('payments')
          .select('*, users(firstname, lastname, email, phone)')
          .in('id', paymentIds);
        
        data = enrichResult.data;
        error = enrichResult.error;
        
        // Appliquer le filtre de recherche textuelle si nécessaire
        if (search && data) {
          const searchLower = search.toLowerCase();
          data = data.filter(p => {
            const user = p.users || {};
            return (
              (user.firstname && user.firstname.toLowerCase().includes(searchLower)) ||
              (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
              (user.email && user.email.toLowerCase().includes(searchLower)) ||
              (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
              (p.transaction_id && p.transaction_id.toLowerCase().includes(searchLower)) ||
              (p.amount && String(p.amount).includes(searchLower)) ||
              (p.payment_status && p.payment_status.toLowerCase().includes(searchLower)) ||
              (p.payment_method && p.payment_method.toLowerCase().includes(searchLower))
            );
          });
        }
      } else {
        // Utiliser la requête standard si aucun filtre spécifique aux documents n'est sélectionné
        let query = supabase.from('payments')
          .select('*, users(firstname, lastname, email, phone)');
        
        // Filtrer par date si spécifié
        if (exportModal.startDate && exportModal.endDate) {
          query = query
            .gte('created_at', format(new Date(exportModal.startDate), 'yyyy-MM-dd'))
            .lte('created_at', format(new Date(exportModal.endDate), 'yyyy-MM-dd'));
        }
        
        // Appliquer les filtres de statut et méthode
        if (statusFilter) {
          query = query.eq('payment_status', statusFilter);
        }
        
        if (methodFilter) {
          query = query.eq('payment_method', methodFilter);
        }
        
        const result = await query;
        data = result.data;
        error = result.error;
        
        // Appliquer le filtre de recherche textuelle si nécessaire
        if (search && data) {
          const searchLower = search.toLowerCase();
          data = data.filter(p => {
            const user = p.users || {};
            return (
              (user.firstname && user.firstname.toLowerCase().includes(searchLower)) ||
              (user.lastname && user.lastname.toLowerCase().includes(searchLower)) ||
              (user.email && user.email.toLowerCase().includes(searchLower)) ||
              (user.phone && user.phone.toLowerCase().includes(searchLower)) ||
              (p.transaction_id && p.transaction_id.toLowerCase().includes(searchLower)) ||
              (p.amount && String(p.amount).includes(searchLower)) ||
              (p.payment_status && p.payment_status.toLowerCase().includes(searchLower)) ||
              (p.payment_method && p.payment_method.toLowerCase().includes(searchLower))
            );
          });
        }
      }
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        toast.error('Aucune donnée à exporter avec les filtres sélectionnés.');
        return;
      }
      
      // Log pour débogage
      console.log('Données reçues pour export:', data);
      
      // Vérifier que les données sont valides avant de les traiter
      const exportedData = data.map(payment => {
        // Vérifier si l'utilisateur existe et a toutes les propriétés nécessaires
        const user = payment.users || {};
        const firstname = user.firstname || '';
        const lastname = user.lastname || '';
        const email = user.email || '';
        const phone = user.phone || '—';
        
        return {
          ID: payment.transaction_id || '',
          Montant: `${payment.amount || 0} €`,
          Statut: payment.payment_status || '',
          Methode: payment.payment_method || '',
          DateCreation: payment.created_at || '',
          Utilisateur: `${firstname} ${lastname}`.trim() || 'Utilisateur inconnu',
          Email: email,
          Téléphone: phone
        };
      });
      
      // Log pour débogage
      console.log('Données formatées pour export:', exportedData);
      
      // Utilisation explicite de l'état exportData
      setExportData(exportedData);
      exportDataRef.current = exportedData;
      
      // Toujours procéder à l'export car nous avons déjà vérifié que data n'est pas vide
        try {
          switch (exportModal.exportFormat) {
            case 'csv': {
              const csvData = [
                Object.keys(exportDataRef.current[0]),
                ...exportDataRef.current.map(obj => Object.values(obj))
              ];
              const csvBlob = new Blob([csvData.map(e => e.join(',')).join('\n')], { type: 'text/csv' });
              const csvLink = document.createElement('a');
              csvLink.href = URL.createObjectURL(csvBlob);
              csvLink.download = 'paiements_export.csv';
              csvLink.click();
              toast.success('Export CSV réussi');
              break;
            }
            case 'pdf': {
              try {
                // Créer un nouveau document PDF en mode paysage
                const doc = new jsPDF({
                  orientation: 'landscape',
                  unit: 'mm',
                  format: 'a4'
                });
                
                // Définir les couleurs de la marque
                const primaryColor = [232, 140, 0]; // Orange #e88c00
                const secondaryColor = [11, 142, 54]; // Vert #0b8e36
                
                // Ajouter un en-tête avec le titre
                doc.setTextColor(...primaryColor);
                doc.setFontSize(18);
                doc.text('Rapport des Paiements', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
                
                // Ajouter la période d'exportation
                const dateRange = exportModal.startDate && exportModal.endDate
                  ? `Période: ${format(new Date(exportModal.startDate), 'dd/MM/yyyy')} - ${format(new Date(exportModal.endDate), 'dd/MM/yyyy')}`
                  : 'Toutes les périodes';
                
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(12);
                doc.text(dateRange, doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' });
                
                // Ajouter les filtres appliqués
                const filters = [];
                if (statusFilter) filters.push(`Statut: ${statusFilter}`);
                if (methodFilter) filters.push(`Méthode: ${methodFilter}`);
                if (search) filters.push(`Recherche: ${search}`);
                
                if (filters.length > 0) {
                  doc.setFontSize(10);
                  doc.setTextColor(100, 100, 100);
                  doc.text(`Filtres appliqués: ${filters.join(', ')}`, doc.internal.pageSize.getWidth() / 2, 28, { align: 'center' });
                }
                
                // Ajouter le nombre total de paiements
                doc.setFontSize(11);
                doc.setTextColor(...secondaryColor);
                doc.text(`Total: ${exportDataRef.current.length} paiement${exportDataRef.current.length > 1 ? 's' : ''}`, doc.internal.pageSize.getWidth() / 2, 34, { align: 'center' });
                
                // Préparer les données pour le tableau
                const tableColumns = Object.keys(exportDataRef.current[0]);
                const tableRows = exportDataRef.current.map(obj => Object.values(obj));
                
                // Définir les dimensions et positions du tableau
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                const margin = 10;
                const tableTop = 40;
                const tableWidth = pageWidth - (2 * margin);
                
                // Définir des largeurs de colonnes personnalisées (en pourcentage du tableWidth)
                const colWidthPercentages = {
                  ID: 18,           // Plus large pour les IDs de transaction
                  Montant: 8,       // Montant avec symbole €
                  Statut: 8,        // Statut de paiement
                  Methode: 8,       // Méthode de paiement
                  DateCreation: 12, // Date de création
                  Utilisateur: 15,  // Nom de l'utilisateur
                  Email: 20,        // Email (plus large)
                  Téléphone: 11     // Numéro de téléphone
                };
                
                // Calculer la largeur de chaque colonne en fonction des pourcentages
                const colWidths = tableColumns.map(col => (colWidthPercentages[col] || 10) * tableWidth / 100);
                
                // Dessiner l'en-tête du tableau
                doc.setFillColor(240, 240, 240);
                doc.setDrawColor(200, 200, 200);
                doc.rect(margin, tableTop, tableWidth, 10, 'FD');
                
                doc.setTextColor(50, 50, 50);
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                
                // Calculer la position X de chaque colonne
                let xPositions = [];
                let currentX = margin;
                
                colWidths.forEach(width => {
                  xPositions.push(currentX);
                  currentX += width;
                });
                
                // Afficher les en-têtes de colonnes
                tableColumns.forEach((column, i) => {
                  doc.text(column, xPositions[i] + 2, tableTop + 6);
                });
                
                // Dessiner les lignes du tableau
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                
                let yPos = tableTop + 10;
                const rowHeight = 8;
                
                // Limiter à 20 lignes par page pour éviter les débordements
                const rowsPerPage = 20;
                const totalPages = Math.ceil(tableRows.length / rowsPerPage);
                
                for (let pageNum = 0; pageNum < totalPages; pageNum++) {
                  if (pageNum > 0) {
                    doc.addPage();
                    yPos = tableTop;
                    
                    // Répéter l'en-tête sur les nouvelles pages
                    doc.setFillColor(240, 240, 240);
                    doc.rect(margin, tableTop, tableWidth, 10, 'FD');
                    
                    doc.setTextColor(50, 50, 50);
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    
                    // Afficher les en-têtes de colonnes
                    tableColumns.forEach((column, i) => {
                      doc.text(column, xPositions[i] + 2, tableTop + 6);
                    });
                    
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(8);
                    
                    yPos = tableTop + 10;
                  }
                  
                  // Déterminer les lignes à afficher sur cette page
                  const startRow = pageNum * rowsPerPage;
                  const endRow = Math.min((pageNum + 1) * rowsPerPage, tableRows.length);
                  
                  for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
                    const row = tableRows[rowIndex];
                    
                    // Alterner les couleurs de fond des lignes
                    if (rowIndex % 2 === 0) {
                      doc.setFillColor(250, 250, 250);
                      doc.rect(margin, yPos, tableWidth, rowHeight, 'F');
                    }
                    
                    // Dessiner les cellules
                    doc.setTextColor(0, 0, 0);
                    // eslint-disable-next-line no-loop-func
                    row.forEach((cell, cellIndex) => {
                      // Convertir la cellule en texte
                      const cellText = String(cell || '');
                      
                      // Traitement spécial pour l'ID de transaction (première colonne)
                      if (cellIndex === 0 && cellText.length > 15) {
                        // Couper l'ID en deux parties pour l'afficher sur deux lignes
                        const firstPart = cellText.substring(0, 15);
                        const secondPart = cellText.substring(15);
                        doc.text(firstPart, xPositions[cellIndex] + 2, yPos + 3);
                        doc.text(secondPart, xPositions[cellIndex] + 2, yPos + 7);
                      } else {
                        // Pour les autres colonnes, tronquer si nécessaire
                        const maxLength = cellIndex === 0 ? 30 : 20; // Plus long pour l'ID
                        const truncatedText = cellText.substring(0, maxLength);
                        doc.text(truncatedText, xPositions[cellIndex] + 2, yPos + 5);
                      }
                    });
                    
                    // Dessiner les lignes horizontales
                    doc.setDrawColor(220, 220, 220);
                    doc.line(margin, yPos + rowHeight, margin + tableWidth, yPos + rowHeight);
                    
                    yPos += rowHeight;
                  }
                  
                  // Ajouter le pied de page
                  doc.setFontSize(8);
                  doc.setTextColor(100, 100, 100);
                  doc.text(`Exporté le ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, margin, pageHeight - 10);
                  doc.text(`Page ${pageNum + 1} / ${totalPages}`, pageWidth - margin - 25, pageHeight - 10);
                }
                
                // Enregistrer le PDF
                doc.save('paiements_export.pdf');
                toast.success('Export PDF réussi');
              } catch (error) {
                console.error('Erreur lors de la génération du PDF:', error);
                toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
              }
              break;
            }
            case 'xls': {
              const ws = XLSX.utils.json_to_sheet(exportDataRef.current);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Paiements');
              XLSX.writeFile(wb, 'paiements_export.xlsx');
              toast.success('Export Excel réussi');
              break;
            }
            default:
              toast('Format d\'exportation non supporté', { icon: '⚠️' });
          }
        } catch (exportError) {
          console.error('Erreur pendant la génération du fichier d\'export:', exportError);
          toast.error(`Erreur pendant l'export: ${exportError.message}`);
        }
      // La vérification des données vides est déjà faite plus haut
      
      setExportModal(prev => ({ ...prev, open: false }));
    } catch (err) {
      console.error('Erreur lors de l\'exportation', err);
      toast.error(`Erreur lors de l'exportation: ${err.message}`);
    }
  };
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 font-semibold">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">Gestion des paiements</h2>
          {(search || statusFilter || methodFilter) ? (
            <span className="text-gray-500">
              {payments.length} résultat{payments.length > 1 ? 's' : ''} sur {totalPayments} paiements
            </span>
          ) : (
            <span className="text-gray-500">{totalPayments} paiements au total</span>
          )}
          {isServerFiltered && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Filtrage optimisé
            </span>
          )}
          <div className="bg-[#e88c00] text-white px-3 py-1 rounded-full flex items-center">
            <span className="font-bold text-lg">{payments.length}</span>
            <span className="ml-1 text-sm">paiement{payments.length > 1 ? 's' : ''}</span>
          </div>
        </div>
        <button
          className="bg-[#0b8e36] hover:bg-[#0b8e36]/90 text-white px-4 py-2 rounded flex items-center gap-1"
          onClick={() => setExportModal(prev => ({ ...prev, open: true }))}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          Exporter
        </button>
      </div>
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Nom, email, téléphone, montant..."
            value={search}
            onChange={handleSearch}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#e88c00] focus:outline-none w-64"
          />
          <select
            value={statusFilter}
            onChange={handleStatusFilter}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="succeeded">Payé</option>
            <option value="pending">En attente</option>
            <option value="failed">Échoué</option>
          </select>
          <select
            value={methodFilter}
            onChange={handleMethodFilter}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="">Toutes les méthodes</option>
            <option value="card">Carte</option>
            <option value="cash">Espèces</option>
            <option value="transfer">Virement bancaire</option>
          </select>

          <button
            className="bg-[#e88c00] hover:bg-[#e88c00]/90 text-white px-4 py-2 rounded flex items-center gap-1"
            onClick={() => {
              // Appliquer les filtres avec le filtrage côté serveur
              applyFilters(statusFilter, methodFilter);
            }}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Filtrer
          </button>
          {isServerFiltered && (
            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
              Filtrage serveur actif
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Résultats/page:</span>
          <select
            value={perPage}
            onChange={handlePerPage}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="15">15 par page</option>
            <option value="30">30 par page</option>
            <option value="50">50 par page</option>
          </select>
        </div>
      </div>
      {exportModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-[500px]">
            <h2 className="text-xl font-bold mb-4">Exporter les paiements</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block mb-2">Date de début</label>
                <input
                  type="date"
                  value={exportModal.startDate}
                  onChange={(e) => setExportModal(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2">Date de fin</label>
                <input
                  type="date"
                  value={exportModal.endDate}
                  onChange={(e) => setExportModal(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2">Service</label>
                <select
                  value={exportModal.selectedService}
                  onChange={(e) => {
                    const selectedService = e.target.value;
                    setExportModal(prev => ({
                      ...prev,
                      selectedService,
                      selectedCategory: '', // Réinitialiser la catégorie
                      selectedAct: '' // Réinitialiser l'acte
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Tous les services</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-2">Catégorie</label>
                <select
                  value={exportModal.selectedCategory}
                  onChange={(e) => {
                    const selectedCategory = e.target.value;
                    setExportModal(prev => ({
                      ...prev,
                      selectedCategory,
                      selectedAct: '' // Réinitialiser l'acte
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                  disabled={!exportModal.selectedService}
                >
                  <option value="">Toutes les catégories</option>
                  {categories
                    .filter(cat => !exportModal.selectedService || cat.service_id === exportModal.selectedService)
                    .map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  }
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-2">Acte consulaire</label>
                <select
                  value={exportModal.selectedAct}
                  onChange={(e) => setExportModal(prev => ({ ...prev, selectedAct: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                  disabled={!exportModal.selectedService && !exportModal.selectedCategory}
                >
                  <option value="">Tous les actes</option>
                  {acts
                    .filter(act =>
                      (!exportModal.selectedService || act.categories.service_id === exportModal.selectedService) &&
                      (!exportModal.selectedCategory || act.category_id === exportModal.selectedCategory)
                    )
                    .map(act => (
                      <option key={act.id} value={act.id}>{act.title}</option>
                    ))
                  }
                </select>
              </div>
              <div className="mb-4 col-span-2">
                <label className="block mb-2">Format d&apos;exportation</label>
                <select
                  value={exportModal.exportFormat}
                  onChange={(e) => setExportModal(prev => ({ ...prev, exportFormat: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                  <option value="xls">XLS</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                className="bg-gray-300 text-black px-4 py-2 rounded"
                onClick={() => setExportModal(prev => ({ ...prev, open: false }))}
              >
                Annuler
              </button>
              <button
                className="bg-green-500 text-white px-4 py-2 rounded"
                onClick={handleExport}
              >
                Exporter
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Tableau des paiements réels */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Méthode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut paiement</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expédition</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPayments.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-gray-500">Aucun paiement trouvé.</td>
              </tr>
            )}
            {paginatedPayments.map((payment) => {
              const user = payment.users || {};
              const shipment = (Array.isArray(payment.shipments) ? payment.shipments[0] : payment.shipments) || null;
              return (
                <tr key={payment.id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : ''}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold flex items-center gap-1">
                        {/* Icône utilisateur */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.7 0 4.5-1.8 4.5-4.5S14.7 3 12 3 7.5 4.8 7.5 7.5 9.3 12 12 12zm0 2c-3 0-9 1.5-9 4.5V21h18v-2.5c0-3-6-4.5-9-4.5z"/></svg>
                        {user.firstname} {user.lastname}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        {/* Icône email */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="currentColor" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                        {user.email}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        {/* Icône téléphone */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1v3.5a1 1 0 01-1 1C7.61 22 2 16.39 2 9.5A1 1 0 013 8.5h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.59a1 1 0 01-.24 1.01l-2.2 2.19z"/></svg>
                        {user.phone}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{Number(payment.amount).toFixed(2)} <span className="text-xs text-gray-500 font-semibold">€</span></td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.payment_method === 'card' ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-[#0b8e36]">
                        {/* Carte bancaire */}
                        <svg className="h-4 w-4 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M2 10h20"/></svg>
                        Carte
                      </span>
                    ) : payment.payment_method}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {payment.payment_status === 'succeeded' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-800 font-semibold text-xs">
                        {/* Check icon */}
                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
                        Payé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-semibold text-xs">
                        {payment.payment_status}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {shipment ? (
                      <button
                        className="px-4 py-2 bg-secondary text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center gap-1"
                        onClick={() => handleShowShipment(shipment.id)}
                        title="Voir l'expédition"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Détails expédition
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Non expédié</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <Link to={`/admin/payments/${payment.id}`} className="text-white bg-[#e88c00] hover:bg-[#e88c00]/90 text-white px-4 py-2 rounded flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      Détails
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <button
            className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-semibold disabled:opacity-50"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Précédent
          </button>
          <span className="font-semibold text-sm">Page {page} / {totalPages}</span>
          <button
            className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-semibold disabled:opacity-50"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Suivant
          </button>
        </div>
      )}
      {/* Modale d'informations d'expédition */}
      <Transition show={shipmentModal.open} as={React.Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={handleCloseShipment}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* Centrer le modal */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-5xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="bg-gradient-to-r from-[#0b8e36] to-[#e88c00] p-4 text-white">
                  <div className="flex justify-between items-center">
                    <Dialog.Title
                      as="h3"
                      className="text-xl font-semibold leading-6 text-white flex items-center gap-2"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      Détails de l&apos;expédition
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-white hover:text-gray-200 transition-colors"
                      onClick={handleCloseShipment}
                    >
                      <span className="sr-only">Fermer</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  {shipmentModal.loading ? (
                    <div className="flex justify-center items-center h-40">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#e88c00]"></div>
                    </div>
                  ) : shipmentModal.error ? (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-600 text-center font-medium">{shipmentModal.error}</div>
                  ) : shipmentModal.shipment ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {/* Colonne Statut & Tracking */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                          <span className="font-bold text-lg text-[#0b8e36]">Statut de l&apos;expédition</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Numéro de suivi</div>
                              <div className="font-semibold">
                                {shipmentModal.shipment.tracking_number && shipmentModal.shipment.tracking_number.startsWith('CD')
                                  ? 'En attente'
                                  : shipmentModal.shipment.tracking_number || 'Non spécifié'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zm10 0a2 2 0 11-4 0 2 2 0 014 0z" /><path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10" /><path d="M1 8h4" /><path d="M16 8h4M16 16h2a2 2 0 002-2v-3a4 4 0 00-4-4h-2" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Transporteur</div>
                              <div className="font-semibold">{shipmentModal.shipment.carrier}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 min-h-5 min-w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Statut</div>
                              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(shipmentModal.shipment.status)}`}>
                                {shipmentModal.shipment.status === 'completed' ? 'Livré' : shipmentModal.shipment.status === 'pending' ? 'En cours' : 'Problème'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Destinataire */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Destinataire</span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0112 0v2"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Nom</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_name}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"/><circle cx="12" cy="11" r="2.5"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Adresse</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_address}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                            <div className="bg-[#e88c00]/10 p-2 rounded-full">
                              <svg className="h-5 w-5 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92V19a2 2 0 01-2.18 2A19.72 19.72 0 013 5.18 2 2 0 015 3h2.09a2 2 0 012 1.72c.13.81.36 1.6.68 2.36a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l1.72-1.72a2 2 0 012.11-.45c.76.32 1.55.55 2.36.68a2 2 0 011.72 2V19z"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium">Téléphone</div>
                              <div className="font-semibold">{shipmentModal.shipment.recipient_phone || '—'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Colonne Documents */}
                      <div className="bg-gray-50 p-5 rounded-lg shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                          <svg className="h-6 w-6 text-[#e88c00]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                          <span className="font-bold text-lg text-[#e88c00]">Documents à expédier</span>
                        </div>
                        
                        {(() => {
                          let docs = shipmentModal.shipment.documents_to_be_sent;
                          if (typeof docs === 'string') {
                            try {
                              docs = JSON.parse(docs);
                            } catch {
                              docs = [];
                            }
                          }
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <div className="space-y-2">
                                {docs.map((doc, idx) => (
                                  <div key={doc.act_id || idx} className="flex items-center gap-3 p-2 bg-white rounded-md shadow-sm">
                                    <div className="bg-[#0b8e36]/10 p-2 rounded-full">
                                      <svg className="h-5 w-5 text-[#0b8e36]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8h8M8 12h6M8 16h8"/></svg>
                                    </div>
                                    <div className="font-medium">{doc.act_title || (typeof doc === 'string' ? doc : JSON.stringify(doc))}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center justify-center h-24 bg-white rounded-md shadow-sm">
                              <span className="text-gray-400 italic">Aucun document</span>
                            </div>
                          );
                        })()}
                        
                        {shipmentModal.shipment.return_status && (
                          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-100 flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-full">
                              <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 110 8h-1"/></svg>
                            </div>
                            <div>
                              <div className="text-xs text-red-500 font-medium">Statut de retour</div>
                              <div className="font-semibold text-red-600">Retourné {shipmentModal.shipment.return_reason && <span>({shipmentModal.shipment.return_reason})</span>}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
                
                <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100">
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-[#0b8e36] border border-transparent rounded-md hover:bg-[#097a2e] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0b8e36] transition-colors"
                    onClick={handleCloseShipment}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PaymentsList;
